import { Notify } from "@digitallab/grid-common-frontend-components";

import {
  allTableColumn,
  listOfBooleanRequired,
  listOfCheckBoxFilters,
  listOfDateRequired,
  listOfFieldsNoFilter,
  listOfFieldsNotSortable,
  listOfFieldsSortable,
  listOfFilterBlankRequired,
  listOfIntBooleanRequired,
  listOfNumberFilter
} from "../../constants";
import DATA_MODEL_TABLE from "../../utils/constants/dataModelTable";
import { convertToCamelCase, convertToSnakeCase } from "../../utils/helpers/text";
import { getFieldValue } from "@digitallab/grid-common-frontend-components";
import BooleanIntRender from "./BooleanIntRender";
import BooleanRender from "./BooleanRender";
import ChipRender from "./ChipRender";
import DateRender from "./DateRender";
import LinkedInstanceRender from "./LinkedInstanceRender";
import { MY_LAB, SOP_RT, TOOL_PANELS } from "../../components/DLabGrid/dLabGridConstant";
import { filter, find, keyBy } from "lodash";
import EquipmentStatusRender from "../log-book/log-book-table/EquipmentStatusRender";
import { OPENSEARCH } from "../../constants";

/**
 * Restores the schema metadata a stored layout entry cannot carry.
 *
 * A saved layout holds only {key, val, order, show, sortStatus} — it is a persistence record, not
 * a column definition. A schema-derived column also carries `filter`, `filterValues` and
 * `renderSlot`. Order and visibility come from what the user saved; everything else comes from the
 * schema.
 *
 * Without this, `getColumnObj` sees no `filterValues` for any *visible* column and falls back to
 * `masterDataFilters`, which has no entry for a schema column that predates it — so a set filter
 * renders empty as soon as a layout is saved. Only the visible columns are affected, which is what
 * makes it hard to spot. This cost instrument-repository the same bug.
 *
 * @param {Object} schemaColumns - Column data keyed by column key, from `keyBy(allTableColumn(), "key")`.
 * @param {Object} storedColumn - One entry of a saved layout.
 * @returns {Object} The schema column carrying the stored order and visibility, or the stored
 *   entry unchanged when the schema no longer knows the column.
 */
export const rehydrateStoredColumn = (schemaColumns, storedColumn) =>
  schemaColumns[storedColumn.key] ?
    { ...schemaColumns[storedColumn.key], order: storedColumn.order, show: storedColumn.show } :
    storedColumn;

/**
 * Turns the grid's current columns into the persisted layout shape.
 *
 * Written from the same list the columns are read from. Sourcing val/show from `DATA_MODEL_TABLE`
 * instead would drop every schema column that has no `DATA_MODEL_TABLE` entry — eleven of them,
 * `isHazardous` and `equipmentType` among them — as "ignore", so a user who enabled one would find
 * it gone from the saved layout.
 *
 * `sortStatus` comes from the hand-written `listOfFieldsSortable` rather than the schema, as it
 * always has: it feeds the persisted value that `DEFAULT_COLUMNS` is compared against, so it must
 * not move.
 *
 * @param {Array} gridColumns - The grid's columns, each with a `colId` and `hide`.
 * @returns {{showColumns: Array, hideColumns: Array}} The layout's two halves.
 */
export const buildDisplayDefinitionFromColumns = (gridColumns = []) => {
  const savedSchemaColumns = keyBy(allTableColumn(), "key");
  const updatedSavedColumns = gridColumns.map((column, index) => {
    const dataModelKey = convertToCamelCase(column?.colId);
    const schemaColumn = savedSchemaColumns[dataModelKey];
    return {
      key: dataModelKey,
      val: schemaColumn?.val || "ignore",
      order: index,
      // "ignore" still marks a colId that is not a data column — the actions column, for one.
      show: schemaColumn ? !column?.hide : "ignore",
      sortStatus: listOfFieldsSortable.includes(dataModelKey)
    };
  });

  return {
    showColumns: filter(updatedSavedColumns, { show: true }),
    hideColumns: filter(updatedSavedColumns, { show: false })
  };
};

export const getColumnObj = (value, masterDataFilters, autoGroupField = null) => {
  let colObj = value;

  colObj = {
    ...colObj,
    sortable: !listOfFieldsNotSortable()?.includes(value.key)
  };

  if (value.key === DATA_MODEL_TABLE?.linkedInstance?.key) {
    colObj = {
      ...colObj,
      cellRenderer: LinkedInstanceRender
    };
  }

  if (value.key === DATA_MODEL_TABLE?.tags?.key) {
    colObj = {
      ...colObj,
      cellRenderer: ChipRender
    };
  }

  if (value.key === DATA_MODEL_TABLE?.status?.key) {
    colObj = {
      ...colObj,
      cellRenderer: EquipmentStatusRender
    };
  }

  if (listOfDateRequired()?.includes(value.key)) {
    colObj = {
      ...colObj,
      cellClass: "action-render",
      cellRenderer: DateRender,
      filter: "agDateColumnFilter",
      filterParams: {
        filterOptions: ["equals", "lessThan", "greaterThan", "inRange"],
        inRangeFloatingFilterDateFormat: "DD-MMM-YYYY",
        suppressAndOrCondition: true,
        defaultJoinOperator: "OR",
        buttons: ["reset", "apply"]
      }
    };
  } else if (listOfBooleanRequired()?.includes(value.key)) {
    // The options must read the way the cells do, or the user picks a value the column never
    // shows. `listOfIntBooleanRequired` names the columns `BooleanRender` renders as Yes/No;
    // `normalizeBooleanFilters` turns that choice back into the 1/0 the index holds.
    const isIntBoolean = listOfIntBooleanRequired?.includes(value.key);
    colObj = {
      ...colObj,
      cellClass: "action-render",
      cellRenderer: BooleanRender,
      filter: "agSetColumnFilter",
      filterParams: {
        defaultToNothingSelected: true,
        showTooltips: true,
        buttons: ["reset"],
        values: (params) => {
          const values =
            isIntBoolean ?
              ["Yes", "No"] :
              ["true", "false"];
          params.success(values);
        }
      }
    };

    // Keeps the grid's own value in step with what the cell and the filter show, so a CSV export
    // and a quick search read Yes/No rather than the raw 1/0. A missing value is left missing
    // rather than mapped to "No", matching the dash `BooleanRender` shows for it — these two
    // fields are absent for every row on CloudSearch, and exporting them all as "No" would assert
    // something the row never said.
    if (isIntBoolean) {
      const fieldName = convertToSnakeCase(value.key);
      colObj = {
        ...colObj,
        valueGetter: (params) => {
          const fieldValue = getFieldValue(params?.data, fieldName);
          // "null" is the string the index returns for an empty field, and it is truthy —
          // BooleanIntRender would read it as NaN and answer "No".
          if (
            fieldValue === undefined ||
            fieldValue === null ||
            fieldValue === "" ||
            fieldValue === "null"
          ) {
            return undefined;
          }
          return BooleanIntRender(fieldValue);
        }
      };
    }
  } else if (listOfCheckBoxFilters()?.includes(value.key)) {
    // The column describes where its own values come from, so no app-side allow-list is
    // needed: `filterValues` is present only when the schema resolved real options for it,
    // and `dynamicValuesKey` names the master-data entry to fall back to otherwise.
    const useSchemaValues = value.filterValues?.length > 0;
    colObj = {
      ...colObj,
      filter: "agSetColumnFilter",
      filterParams: {
        buttons: ["reset"],
        defaultToNothingSelected: true,
        values: (params) => {
          const values =
            useSchemaValues ?
              value.filterValues :
              masterDataFilters[value.dynamicValuesKey ?? value.key] || [];
          params.success(values);
        }
      }
    };
  } else if (listOfNumberFilter()?.includes(value.key)) {
    colObj = {
      ...colObj,
      filter: "agNumberColumnFilter",
      filterParams: {
        filterOptions: ["equals", "lessThan", "greaterThan", "inRange"],
        defaultJoinOperator: "OR",
        buttons: ["reset", "apply"]
      }
    };
  } else if (!listOfFieldsNoFilter().includes(value.key)) {
    colObj = {
      ...colObj,
      filter: "agTextColumnFilter",
      filterParams: {
        filterOptions: listOfFilterBlankRequired()?.includes(value.key) ? ["contains", "blank"] : ["contains"],
        buttons: ["reset", "apply"],
        defaultToNothingSelected: true,
        defaultJoinOperator: "OR",
        values: (params) => {
          const values = masterDataFilters[value.key] || [];
          params.success(values);
        }
      }
    };
  }

  if (autoGroupField && value.key === autoGroupField) {
    colObj = {
      ...colObj,
      hide: true,
      lockVisible: true
    };
  }

  return colObj;
};

export const getColumnObjClient = (value) => {
  let colObj = value;

  colObj = {
    ...colObj,
    sortable: !listOfFieldsNotSortable()?.includes(value.key)
  };
  if (listOfBooleanRequired()?.includes(value.key)) {
    colObj = {
      ...colObj,
      cellClass: "action-render",
      cellRenderer: BooleanRender
    };
  }

  if (value.key === DATA_MODEL_TABLE?.linkedInstance?.key) {
    colObj = {
      ...colObj,
      cellRenderer: LinkedInstanceRender
    };
  }

  if (value.key === DATA_MODEL_TABLE?.tags?.key) {
    colObj = {
      ...colObj,
      cellRenderer: ChipRender
    };
  }

  if (listOfDateRequired()?.includes(value.key)) {
    colObj = {
      ...colObj,
      cellClass: "action-render",
      cellRenderer: DateRender
    };
  }

  return colObj;
};
//======================FILTER FUNCTIONC===================
export const onUpdateFilter = async ({
  lastFavFilter,
  isSaveConfigUpdated = false,
  updateRedux = true,
  id,
  email,
  client,
  query,
  userProfileQuery,
  updateLastFavFilter = null
}) => {
  let lastFilterData = {};
  lastFilterData.lastFavFilter = lastFavFilter;
  lastFilterData = JSON.stringify(lastFilterData);
  let result = null;
  try {
    result = await client.mutate({
      mutation: userProfileQuery,
      variables: { id, email, lastFilter: lastFilterData }
    });

    if (isSaveConfigUpdated) {
      const filterDefinition =
        typeof lastFavFilter?.filterDefinition === "string"
          ? lastFavFilter?.filterDefinition
          : JSON.stringify(lastFavFilter?.filterDefinition);
      await client.mutate({
        mutation: query,
        variables: {
          userID: id,
          filterName: lastFavFilter?.filterName,
          filterDefinition
        },
        fetchPolicy: "no-cache"
      });
    }

    if (isSaveConfigUpdated) {
      Notify({
        type: "success",
        icon: "circle_confirm",
        title: "",
        text: `Updated filter successfully `
      });
    }
  } catch (error) {
    if (isSaveConfigUpdated) {
      Notify({
        type: "warning",
        icon: "alarm",
        title: "",
        text: "Failed to update filter"
      });
    }
    result = null;
  }
  if (updateRedux && updateLastFavFilter) {
    updateLastFavFilter(lastFavFilter);
  }
  return result;
};

export const onCreatefilter = async ({
  id,
  email,
  client,
  userFilters,
  lastFavFilter,
  query,
  userProfileQuery,
  updateUserFilters = null,
  updateLastFavFilter = null
}) => {
  let result = null;
  try {
    const FilterNameMatch = userFilters.filter((item) => item.filterName === lastFavFilter?.filterName?.trim());
    if (FilterNameMatch.length === 0 && lastFavFilter?.filterName !== MY_LAB) {
      result = await client.mutate({
        mutation: query,
        variables: {
          userID: id,
          filterDefinition: JSON.stringify(lastFavFilter?.filterDefinition),
          filterName: lastFavFilter.filterName?.trim()
        }
      });
      if (result) {
        await onUpdateFilter({
          lastFavFilter,
          updateLastFavFilter,
          userProfileQuery,
          id,
          email,
          client
        });
        if (updateUserFilters) {
          updateUserFilters([...userFilters, lastFavFilter]);
        }
      }

      Notify({
        type: "success",
        icon: "circle_confirm",
        title: "",
        text: `Filters successfully saved `
      });
    } else {
      Notify({
        type: "warning",
        icon: "alarm",
        title: "",
        text: `Duplicate filter name `
      });
    }
  } catch (err) {
    console.log(err);
    Notify({
      type: "warning",
      icon: "alarm",
      title: "",
      text: `Filter creation failure `
    });
  }

  return result;
};

//===============COLUMN FUNCTIONS=================
const parseData = (data) => (typeof data === "string" ? JSON.parse(data) : data);

const persistDisplayColumnConfig = async ({ data, query, id, client, userDisplayList, updateUserDisplayColumns }) => {
  const updateData = parseData(data);
  const displayDefinition =
    typeof updateData?.displayDefinition === "string"
      ? updateData?.displayDefinition
      : JSON.stringify(updateData?.displayDefinition);
  await client.mutate({
    mutation: query,
    variables: { userID: id, displayName: updateData?.displayName, displayDefinition },
    fetchPolicy: "no-cache"
  });
  const updatedList = userDisplayList?.map((userDisplay) =>
    parseData(data)?.displayName === userDisplay?.displayName ? parseData(data) : userDisplay
  );
  if (updateUserDisplayColumns) {
    updateUserDisplayColumns([...updatedList]);
  }
};

export const onUpdateSelectColumn = async ({
  data,
  isSaveConfigUpdated = false,
  updateRedux = true,
  id,
  email,
  client,
  userProfileQuery,
  query,
  userDisplayList,
  updateUserDisplayColumns = null,
  updateLastDisplayColumn = null
}) => {
  let result = null;
  try {
    result = await client.mutate({
      mutation: userProfileQuery,
      variables: { id, email, lastDisplayColumns: data },
      fetchPolicy: "no-cache"
    });

    if (isSaveConfigUpdated) {
      await persistDisplayColumnConfig({ data, query, id, client, userDisplayList, updateUserDisplayColumns });
      Notify({
        type: "success",
        icon: "circle_confirm",
        title: "",
        text: `Updated columns set successfully `
      });
    }
  } catch (error) {
    if (isSaveConfigUpdated) {
      Notify({
        type: "warning",
        icon: "alarm",
        title: "",
        text: `Failed to update columns set`
      });
    }
    return null;
  }

  if (updateRedux && updateLastDisplayColumn) {
    const parsedData = parseData(data);
    updateLastDisplayColumn(parsedData?.equipmentList || parsedData);
  }

  return result;
};

export const onCreateSelectColumn = async ({
  id,
  email,
  userDisplayList,
  unParsedData,
  client,
  query,
  userProfileQuery,
  updateUserDisplayColumns = null,
  updateLastDisplayColumn = null
}) => {
  let result = null;

  const columnMatches = userDisplayList.filter((item) => item.displayName === unParsedData?.displayName?.trim());
  if (columnMatches?.length === 0 && unParsedData?.displayName !== "SOP RT0020") {
    let columnObj = {
      displayName: unParsedData?.displayName,
      userID: id,
      displayDefinition: JSON.stringify(unParsedData?.displayDefinition)
    };

    try {
      result = await client.mutate({
        mutation: query,
        variables: columnObj,
        fetchPolicy: "no-cache"
      });

      if (result) {
        await onUpdateSelectColumn({
          data: JSON.stringify({ equipmentList: unParsedData }),
          updateLastDisplayColumn,
          userProfileQuery,
          id,
          email,
          client
        });

        userDisplayList = [...userDisplayList, result?.data?.createUserDisplay];
        if (updateUserDisplayColumns) {
          updateUserDisplayColumns([...userDisplayList]);
        }
      }

      Notify({
        type: "success",
        icon: "circle_confirm",
        title: "",
        text: `Columns Set successfully saved`
      });
    } catch (error) {
      Notify({
        type: "warning",
        icon: "alarm",
        title: "",
        text: `Failed to save columns set`
      });
    }
  } else {
    Notify({
      type: "warning",
      icon: "alarm",
      title: "",
      text: "Duplicate column name"
    });
  }
  return result;
};

//==============onChip delete======================

export const onDeleteSaveConfigurations = async ({
  userID,
  email,
  client,
  chipToDelete,
  panel,
  lastSavedChip,
  userProfileQuery,
  deleteFilterQuery,
  deleteColumnQuery,
  userFilters,
  userDisplayList,
  updateUserFilters = null,
  updateUserDisplayColumns = null,
  updateLastFavFilter = null,
  updateLastDisplayColumn = null
}) => {
  let result = null;
  let variables = { userID: chipToDelete?.userID || userID };
  if (panel === TOOL_PANELS?.filters) {
    variables = {
      ...variables,
      filterName: chipToDelete?.filterName
    };
  } else if (panel === TOOL_PANELS?.columns) {
    variables = {
      ...variables,
      displayName: chipToDelete?.displayName
    };
  }
  try {
    result = await client.mutate({
      mutation: panel === TOOL_PANELS?.filters ? deleteFilterQuery : deleteColumnQuery,
      variables
    });
    if (panel === TOOL_PANELS?.filters) {
      const removeIndex = userFilters.findIndex((item) => item.filterName === chipToDelete?.filterName);
      userFilters.splice(removeIndex, 1);
      if (lastSavedChip?.filterName === chipToDelete?.filterName) {
        await onUpdateFilter({
          lastFavFilter: {
            filterDefinition: chipToDelete?.filterDefinition,
            filterName: ""
          },
          userProfileQuery,
          updateLastFavFilter,
          id: userID,
          email,
          client
        });
      }
      const updateFilter = [...userFilters];
      if (find(updateFilter, { filterName: MY_LAB })) {
        updateFilter.shift();
      }
      if (updateUserFilters) {
        updateUserFilters([...updateFilter]);
      }
    } else {
      const removeIndex = userFilters.findIndex((item) => item.displayName === chipToDelete?.displayName);
      userFilters.splice(removeIndex, 1);
      const savedColumn = typeof lastSavedChip === "string" ? JSON.parse(lastSavedChip) : lastSavedChip;
      if (savedColumn?.displayName === chipToDelete?.displayName) {
        await onUpdateSelectColumn({
          data: JSON.stringify({
            displayDefinition:
              typeof chipToDelete?.displayDefinition === "string"
                ? JSON.parse(chipToDelete?.displayDefinition)
                : chipToDelete?.displayDefinition,
            displayName: ""
          }),
          updateLastDisplayColumn,
          userProfileQuery,
          id: userID,
          email,
          client
        });
      }
      const updateColumns = [...userFilters];
      if (find(updateColumns, { displayName: SOP_RT })) {
        updateColumns.shift();
      }
      userDisplayList = [...updateColumns];
      if (updateUserDisplayColumns) {
        updateUserDisplayColumns([...updateColumns]);
      }
    }
    Notify({
      type: "success",
      icon: "circle_confirm",
      title: "",
      text: panel === TOOL_PANELS?.filters ? "Filter successfully deleted" : "Columns set successfully deleted"
    });
  } catch (err) {
    Notify({
      type: "warning",
      icon: "alarm",
      title: "",
      text: panel === TOOL_PANELS?.filters ? "Filter deletion failure" : "Columns set deletion failure"
    });
  }
  return { result, userFilters, panel };
};

export const saveLastPageSize = async ({ lastPageSize, query, updateLastPageSize, id, email, client }) => {
  try {
    await client.mutate({
      mutation: query,
      variables: { id, email, lastPageSize }
    });
    updateLastPageSize(lastPageSize);
  } catch (err) {
    console.log(" last page size fetch!", { err });
  }
};

export const OPENSEARCH_CONFIGURATION = process.env.REACT_APP_SEARCH_ENGINE?.toUpperCase() === OPENSEARCH;

/**
 * Rewrites the int-boolean columns' filter clauses from what the user picked to what the index
 * holds — `is_hazardous:"Yes"` becomes `is_hazardous:1`.
 *
 * `listOfIntBooleanRequired` names the two columns stored as 0/1 rather than true/false. The set
 * filter offers Yes/No for them so the options read the way the cells do, and this closes the gap
 * on the way out. Without it those two filters match nothing at all.
 *
 * Both spellings are handled: Yes/No is what the filter offers today, and true/false is what a
 * layout saved before this change still carries.
 *
 * Applied on both engines. booking-frontend, which reads the same equipment index, normalizes on
 * its OpenSearch path as well as its CloudSearch one — the 0/1 storage is a property of the field,
 * not of the engine in front of it.
 *
 * @param {string} query - Search or filter query to rewrite.
 * @returns {string} The query with int-boolean clauses expressed as 1/0.
 */
export const normalizeBooleanFilters = (query) => {
  if (!query || typeof query !== "string") {
    return query;
  }

  const fieldNames = listOfIntBooleanRequired
    .filter(Boolean)
    .map((key) => convertToSnakeCase(key))
    .join("|");
  if (!fieldNames) {
    return query;
  }

  const yesNoClauses = new RegExp(`(${fieldNames}): ?\\*?"?(Yes|No)"?\\*?`, "gi");
  const trueFalseClauses = new RegExp(`(${fieldNames}): ?\\*?"?(true|false)"?\\*?`, "gi");

  return query
    .replace(yesNoClauses, (_match, fieldName, value) => `${fieldName}:${value.toLowerCase() === "yes" ? 1 : 0}`)
    .replace(
      trueFalseClauses,
      (_match, fieldName, value) => `${fieldName}:${value.toLowerCase() === "true" ? 1 : 0}`
    );
};
